import { create } from "zustand"
import type { CardPick, Customer, Job, Pain, Participant, Session } from "@/types"

/**
 * `missing` is its own status, distinct from `error`: the listener is healthy
 * and Firestore answered — the session simply does not exist. A mistyped join
 * link, a stale bookmark or a deleted session all land here, and without it they
 * present as a live connection to a session that is null, which reads as
 * "still loading" forever.
 */
export type ConnectionStatus = "idle" | "connecting" | "live" | "missing" | "error"

/**
 * Mirrors the Firestore documents one-to-one. Round, phase, session status,
 * config, timer and lifecycle all live inside `session` rather than being copied
 * to the top level, so realtime snapshots can be written straight in and nothing
 * can drift out of sync. Anything computable (participant counts, vote tallies,
 * progress, whether the session has expired) is derived in `selectors.ts`, never
 * stored.
 */
export interface SessionState {
  customer: Customer | null
  session: Session | null
  participants: Participant[]
  /**
   * Whether the roster listener has delivered at least once.
   *
   * Not derivable: an empty `participants` means either "nobody has joined" or
   * "the snapshot has not arrived", and a screen has to tell those apart. A
   * browser restores its guest id from localStorage instantly, so without this
   * the moment before the roster lands looks exactly like "you are no longer in
   * this session" — and telling a participant they were removed, wrongly, on
   * every reload is worse than a beat of waiting.
   */
  rosterLoaded: boolean
  /**
   * Every pain point written in this session, with its votes. Session-scoped
   * rather than job-scoped: the room votes once, and a job records which of
   * these it came from.
   */
  pains: Pain[]
  /** Same reasoning as `rosterLoaded`: an empty board is not an unloaded one. */
  painsLoaded: boolean
  /** Every job the session has opened, oldest first. The report reads these. */
  jobs: Job[]
  /** The job named by `session.currentJobId`, or null between jobs. */
  job: Job | null
  /** What each person picked for `job`. Empty when there is no current job. */
  picks: CardPick[]
  /** Local identity: which participant this browser is, if any. */
  currentParticipantId: string | null
  /**
   * The signed-in facilitator's uid, mirrored from the auth store.
   *
   * Auth lives in its own store — it outlives any session — but "am I the
   * facilitator of THIS session" is a question about the session document, and
   * every selector here answers from one store. Mirroring one string keeps
   * `selectIsFacilitator` a pure function of this state instead of a hook that
   * has to read two stores and re-render on both.
   *
   * It is not a claim of anything: the answer is `session.facilitatorIds`
   * containing it, and the Firestore rules test the same thing server-side.
   */
  signedInUid: string | null
  /** The session subscription's status. Says nothing about the customer's. */
  connection: ConnectionStatus
  /**
   * The customer subscription's status, tracked apart because the two are
   * independent: the customer screen has no session at all, and a guest inside a
   * session may not read the customer document. Sharing one field made a dead
   * customer listener report the session dead, and a missing customer
   * indistinguishable from a loading one.
   */
  customerConnection: ConnectionStatus
  error: string | null
  /**
   * The customer listener's message, kept out of `error`. `error` is the write
   * channel: ErrorToast shows it as a failed write and the session screen shows
   * it beside a dead session listener. A customer failure is neither, and
   * sharing the field made it surface as a spurious toast and overwrite an
   * unrelated error.
   */
  customerError: string | null

  applyCustomerSnapshot: (customer: Customer | null) => void
  setParticipants: (participants: Participant[]) => void
  setPains: (pains: Pain[]) => void
  setJobs: (jobs: Job[]) => void
  setPicks: (picks: CardPick[]) => void
  setCurrentParticipantId: (participantId: string | null) => void
  setSignedInUid: (uid: string | null) => void
  setConnection: (connection: ConnectionStatus) => void
  setCustomerConnection: (connection: ConnectionStatus) => void
  /** The customer listener's own failure, so one cannot report the other dead. */
  setCustomerError: (message: string) => void
  /**
   * Reports a problem WITHOUT declaring the connection dead. For failed writes:
   * a rejected update means that write did not land, not that the listeners
   * stopped delivering.
   */
  setError: (message: string | null) => void
  /** Reports a listener failure. Terminal: Firestore does not retry these. */
  setConnectionError: (message: string) => void
  /**
   * Applies a realtime session snapshot as one atomic update. Separate setters
   * would notify subscribers several times per snapshot and briefly expose a
   * state where the session had arrived but the connection still read
   * "connecting".
   */
  applySessionSnapshot: (session: Session | null) => void
  /**
   * Applies a job snapshot. Clears `picks` when the job is gone: deleting a
   * document does NOT delete its subcollections, so the picks listener keeps
   * delivering the selections of a job that no longer exists.
   */
  applyJobSnapshot: (job: Job | null) => void
  /** Clears session-scoped data but keeps `customer`. Used when switching sessions. */
  clearSessionData: () => void
  reset: () => void
}

const INITIAL_STATE = {
  customer: null,
  session: null,
  participants: [],
  rosterLoaded: false,
  pains: [],
  painsLoaded: false,
  jobs: [],
  job: null,
  picks: [],
  currentParticipantId: null,
  signedInUid: null,
  connection: "idle" as ConnectionStatus,
  customerConnection: "idle" as ConnectionStatus,
  error: null,
  customerError: null,
}

export const useSessionStore = create<SessionState>()((set) => ({
  ...INITIAL_STATE,

  // One update, for the same reason as applySessionSnapshot: separate setters
  // would briefly expose an arrived customer with a still-"connecting" status.
  applyCustomerSnapshot: (customer) =>
    set((s) => ({
      customer,
      customerConnection:
        s.customerConnection === "error" ? s.customerConnection : customer ? "live" : "missing",
    })),
  setParticipants: (participants) => set({ participants, rosterLoaded: true }),
  setPains: (pains) => set({ pains, painsLoaded: true }),
  setJobs: (jobs) => set({ jobs }),
  setPicks: (picks) => set({ picks }),
  // There is deliberately no plain `setSession`/`setJob`: the two apply*
  // actions below are the only way in, because they are what keeps
  // session/connection and job/picks consistent with each other.
  setCurrentParticipantId: (currentParticipantId) => set({ currentParticipantId }),
  setSignedInUid: (signedInUid) => set({ signedInUid }),
  setConnection: (connection) => set({ connection }),
  setCustomerConnection: (customerConnection) => set({ customerConnection }),
  setCustomerError: (customerError) => set({ customerError, customerConnection: "error" }),
  // Deliberately does not touch `connection`: a failed write leaves the
  // listeners alive, and marking the connection dead would send the screen to an
  // unrecoverable error state over one rejected button press.
  setError: (error) => set({ error }),
  setConnectionError: (error) => set({ error, connection: "error" }),
  // Must not clear `error` or downgrade an "error" connection: Firestore
  // listener failures are terminal, and the session listener keeps delivering
  // snapshots after a *participants* listener has died. Clearing here would
  // report a healthy live session with a permanently frozen roster.
  //
  // A null session means Firestore answered "no such document". Everything
  // session-scoped goes with it — subcollection documents outlive their parent,
  // so the roster, the board and the jobs would otherwise keep rendering under a
  // session that is gone.
  applySessionSnapshot: (session) =>
    set((s) => {
      const connection = s.connection === "error" ? s.connection : session ? "live" : "missing"
      if (session) return { session, connection }
      return {
        session: null,
        participants: [],
        rosterLoaded: false,
        pains: [],
        painsLoaded: false,
        jobs: [],
        job: null,
        picks: [],
        connection,
      }
    }),
  applyJobSnapshot: (job) => set(job ? { job } : { job: null, picks: [] }),
  clearSessionData: () =>
    set({
      session: null,
      participants: [],
      rosterLoaded: false,
      pains: [],
      painsLoaded: false,
      jobs: [],
      job: null,
      picks: [],
      currentParticipantId: null,
      connection: "idle",
      error: null,
    }),
  // `signedInUid` survives a reset: it belongs to the auth store, which is not
  // torn down with a session, and clearing it here would make the facilitator
  // look like a spectator until the next auth callback.
  reset: () => set((s) => ({ ...INITIAL_STATE, signedInUid: s.signedInUid })),
}))
